import { useEffect, useRef, useState, useMemo } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import {
  ScanBarcode, Plus, Minus, Trash2, X, ShoppingCart, Banknote,
  CreditCard, ArrowLeftRight, Layers, ReceiptText, LogOut, History, Settings,
} from "lucide-react";
import { toast } from "sonner";
import Ticket from "@/components/Ticket";

const formatMXN = (n) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n || 0);

export default function POS() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [code, setCode] = useState("");
  const [cart, setCart] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("efectivo");
  const [cashReceived, setCashReceived] = useState("");
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [lastSale, setLastSale] = useState(null);
  const [ticketOpen, setTicketOpen] = useState(false);

  const subtotal = useMemo(
    () => cart.reduce((s, it) => s + it.price * it.quantity, 0),
    [cart]
  );
  const total = Math.max(0, subtotal - (Number(discount) || 0));
  const change =
    (paymentMethod === "efectivo" || paymentMethod === "mixto") && Number(cashReceived) > 0
      ? Math.max(0, Number(cashReceived) - total)
      : 0;

  // Always keep barcode input focused
  useEffect(() => {
    const focus = () => {
      if (!checkoutOpen && !ticketOpen) inputRef.current?.focus();
    };
    focus();
    const id = setInterval(focus, 600);
    return () => clearInterval(id);
  }, [checkoutOpen, ticketOpen]);

  const addItemByBarcode = async (barcode) => {
    if (!barcode.trim()) return;
    try {
      const { data: prod } = await api.get(`/products/by-barcode/${encodeURIComponent(barcode.trim())}`);
      if (prod.stock <= 0) {
        toast.error(`Sin stock: ${prod.name}`);
        return;
      }
      setCart((prev) => {
        const idx = prev.findIndex((it) => it.product_id === prod.id);
        if (idx >= 0) {
          if (prev[idx].quantity + 1 > prod.stock) {
            toast.error(`Stock insuficiente para ${prod.name}`);
            return prev;
          }
          const next = [...prev];
          next[idx] = { ...next[idx], quantity: next[idx].quantity + 1 };
          return next;
        }
        return [
          ...prev,
          {
            product_id: prod.id,
            barcode: prod.barcode,
            name: prod.name,
            price: prod.price,
            quantity: 1,
            stock: prod.stock,
          },
        ];
      });
      toast.success(`+ ${prod.name}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Producto no encontrado");
    }
  };

  const onScanSubmit = (e) => {
    e.preventDefault();
    if (code.trim()) {
      addItemByBarcode(code);
      setCode("");
    }
  };

  const updateQty = (idx, delta) => {
    setCart((prev) => {
      const next = [...prev];
      const it = { ...next[idx] };
      it.quantity = Math.max(1, it.quantity + delta);
      if (it.stock && it.quantity > it.stock) {
        toast.error(`Stock máximo: ${it.stock}`);
        return prev;
      }
      next[idx] = it;
      return next;
    });
  };

  const setQty = (idx, value) => {
    const v = Math.max(1, parseInt(value || "1", 10));
    setCart((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], quantity: v };
      return next;
    });
  };

  const removeItem = (idx) => setCart((prev) => prev.filter((_, i) => i !== idx));
  const clearCart = () => {
    setCart([]);
    setDiscount(0);
    setCashReceived("");
    setPaymentMethod("efectivo");
  };

  const openCheckout = () => {
    if (!cart.length) {
      toast.error("Carrito vacío");
      return;
    }
    setCheckoutOpen(true);
  };

  const finalizeSale = async () => {
    setProcessing(true);
    try {
      const { data } = await api.post("/sales", {
        items: cart.map(({ product_id, barcode, name, price, quantity }) => ({
          product_id, barcode, name, price, quantity,
        })),
        payment_method: paymentMethod,
        cash_received: Number(cashReceived) || 0,
        discount: Number(discount) || 0,
      });
      setLastSale(data);
      setCheckoutOpen(false);
      setTicketOpen(true);
      clearCart();
      toast.success(`Venta ${data.folio} registrada`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Error al registrar venta");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone flex flex-col">
      {/* Top bar */}
      <header className="bg-stone text-bone px-6 h-14 flex items-center justify-between border-b border-stone/20">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-clay flex items-center justify-center font-outfit font-bold text-white">
            L
          </div>
          <div className="font-outfit font-bold tracking-tight">Losería POS</div>
          <Badge className="bg-clay/15 text-clay border-clay/20 hover:bg-clay/20 ml-2 font-figtree">
            Caja activa
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          {user?.role === "admin" && (
            <Link to="/admin">
              <Button variant="ghost" size="sm" data-testid="pos-admin-link" className="text-bone hover:bg-bone/10 hover:text-bone">
                <Settings className="w-4 h-4 mr-2" />
                Administración
              </Button>
            </Link>
          )}
          <Link to="/admin/sales">
            <Button variant="ghost" size="sm" data-testid="pos-history-link" className="text-bone hover:bg-bone/10 hover:text-bone">
              <History className="w-4 h-4 mr-2" />
              Ventas
            </Button>
          </Link>
          <div className="text-xs text-bone/60 px-3 hidden sm:block">{user?.name}</div>
          <Button
            variant="ghost" size="sm"
            data-testid="pos-logout-button"
            onClick={async () => { await logout(); navigate("/login"); }}
            className="text-bone hover:bg-clay hover:text-white"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      {/* Body grid */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-4 p-4">
        {/* Cart area */}
        <section className="lg:col-span-8 flex flex-col gap-4">
          {/* Scanner input */}
          <form onSubmit={onScanSubmit} className="bg-white rounded-xl border border-warm-border p-4 shadow-sm">
            <label className="text-[10px] uppercase tracking-[0.25em] text-stone/50 font-semibold flex items-center gap-2 mb-2">
              <ScanBarcode className="w-3.5 h-3.5 text-clay" />
              Escanea o teclea código de barras
            </label>
            <div className="relative">
              <Input
                ref={inputRef}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Listo para escanear..."
                data-testid="pos-barcode-input"
                autoComplete="off"
                className="h-16 text-2xl font-figtree font-medium px-5 border-2 border-warm-border focus-visible:border-clay focus-visible:ring-4 focus-visible:ring-clay/20 focus-visible:ring-offset-0 rounded-xl bg-bone animate-scan-pulse"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-stone/40">
                <span className="inline-block w-2 h-2 rounded-full bg-clay animate-pulse" />
                Esperando
              </div>
            </div>
          </form>

          {/* Cart list */}
          <div className="bg-white rounded-xl border border-warm-border flex-1 flex flex-col overflow-hidden">
            <div className="px-5 py-3 border-b border-warm-border flex items-center justify-between">
              <div className="flex items-center gap-2 text-stone">
                <ShoppingCart className="w-4 h-4 text-clay" />
                <h2 className="font-outfit font-semibold">Carrito ({cart.length})</h2>
              </div>
              {cart.length > 0 && (
                <Button
                  variant="ghost" size="sm"
                  data-testid="pos-clear-cart"
                  onClick={clearCart}
                  className="text-stone/60 hover:text-clay-hover"
                >
                  <X className="w-3.5 h-3.5 mr-1" /> Vaciar
                </Button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto" data-testid="pos-cart-list">
              {cart.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-stone/40 p-10">
                  <Layers className="w-12 h-12 mb-3 text-stone/20" />
                  <div className="font-outfit text-lg text-stone/60">Carrito vacío</div>
                  <div className="text-sm mt-1 text-center max-w-xs">
                    Escanea un código de barras para añadir productos.
                  </div>
                </div>
              ) : (
                cart.map((it, idx) => (
                  <div
                    key={`${it.product_id}-${idx}`}
                    data-testid={`cart-item-${idx}`}
                    className="px-5 py-3 border-b border-warm-border last:border-0 hover:bg-sand/60 transition-colors flex items-center gap-4"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-figtree font-semibold text-stone truncate">{it.name}</div>
                      <div className="text-xs text-stone/50 font-mono mt-0.5">{it.barcode}</div>
                    </div>
                    <div className="text-sm text-stone/70 w-24 text-right">{formatMXN(it.price)}</div>
                    <div className="flex items-center gap-1 bg-bone rounded-lg p-1 border border-warm-border">
                      <button
                        onClick={() => updateQty(idx, -1)}
                        data-testid={`cart-decrease-${idx}`}
                        className="w-7 h-7 rounded-md hover:bg-warm-border flex items-center justify-center text-stone"
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <input
                        type="number"
                        value={it.quantity}
                        onChange={(e) => setQty(idx, e.target.value)}
                        className="w-10 text-center bg-transparent font-outfit font-semibold focus:outline-none"
                        data-testid={`cart-qty-${idx}`}
                      />
                      <button
                        onClick={() => updateQty(idx, 1)}
                        data-testid={`cart-increase-${idx}`}
                        className="w-7 h-7 rounded-md hover:bg-warm-border flex items-center justify-center text-stone"
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="w-24 text-right font-outfit font-semibold text-stone">
                      {formatMXN(it.price * it.quantity)}
                    </div>
                    <button
                      onClick={() => removeItem(idx)}
                      data-testid={`cart-remove-${idx}`}
                      className="text-stone/40 hover:text-clay transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </section>

        {/* Checkout panel */}
        <aside className="lg:col-span-4 bg-white rounded-xl border border-warm-border p-6 flex flex-col gap-5 shadow-sm">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-stone/50 font-semibold mb-1">
              Total a pagar
            </div>
            <div
              data-testid="pos-total"
              className="font-outfit text-6xl font-bold text-stone tracking-tighter leading-none"
            >
              {formatMXN(total)}
            </div>
          </div>

          <div className="border-t border-warm-border pt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-stone/60">Subtotal</span>
              <span className="font-medium text-stone" data-testid="pos-subtotal">{formatMXN(subtotal)}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <label className="text-stone/60">Descuento</label>
              <Input
                type="number" min="0"
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
                data-testid="pos-discount-input"
                className="h-8 w-28 text-right border-warm-border focus-visible:ring-clay focus-visible:ring-offset-0"
              />
            </div>
            <div className="flex justify-between text-sm pt-2 border-t border-warm-border">
              <span className="text-stone/60">Artículos</span>
              <span className="font-medium text-stone">
                {cart.reduce((s, it) => s + it.quantity, 0)}
              </span>
            </div>
          </div>

          <Button
            onClick={openCheckout}
            disabled={!cart.length}
            data-testid="pos-checkout-button"
            className="mt-auto w-full h-16 bg-clay hover:bg-clay-hover text-white text-xl font-outfit font-bold rounded-xl shadow-[0_4px_14px_rgba(200,90,64,0.39)] hover:-translate-y-0.5 transition-all"
          >
            <ReceiptText className="w-5 h-5 mr-2" />
            Cobrar
          </Button>
        </aside>
      </div>

      {/* Checkout dialog */}
      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent className="sm:max-w-md bg-bone border-warm-border" data-testid="checkout-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-2xl">Cobrar venta</DialogTitle>
            <DialogDescription className="text-stone/60">
              Selecciona el método de pago y registra la venta.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-white border border-warm-border rounded-xl p-4 text-center">
              <div className="text-[10px] uppercase tracking-[0.25em] text-stone/50 mb-1">Total</div>
              <div className="font-outfit text-4xl font-bold text-clay">{formatMXN(total)}</div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-stone/70 mb-2 block">
                Método de pago
              </label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { v: "efectivo", l: "Efectivo", I: Banknote },
                  { v: "tarjeta", l: "Tarjeta", I: CreditCard },
                  { v: "transferencia", l: "Transfer.", I: ArrowLeftRight },
                  { v: "mixto", l: "Mixto", I: Layers },
                ].map(({ v, l, I }) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setPaymentMethod(v)}
                    data-testid={`payment-${v}`}
                    className={`flex items-center gap-2 px-3 py-3 rounded-lg border-2 transition-all ${
                      paymentMethod === v
                        ? "border-clay bg-clay/5 text-clay"
                        : "border-warm-border bg-white text-stone hover:border-stone/30"
                    }`}
                  >
                    <I className="w-4 h-4" />
                    <span className="font-figtree font-medium">{l}</span>
                  </button>
                ))}
              </div>
            </div>

            {(paymentMethod === "efectivo" || paymentMethod === "mixto") && (
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-stone/70 mb-2 block">
                  Efectivo recibido
                </label>
                <Input
                  type="number" min="0"
                  value={cashReceived}
                  onChange={(e) => setCashReceived(e.target.value)}
                  data-testid="cash-received-input"
                  className="h-12 text-lg border-warm-border focus-visible:ring-clay focus-visible:ring-offset-0"
                  placeholder="0.00"
                  autoFocus
                />
                {Number(cashReceived) > 0 && (
                  <div className="mt-2 flex justify-between text-sm bg-sage/10 border border-sage/30 rounded-lg px-3 py-2">
                    <span className="text-stone/70">Cambio</span>
                    <span className="font-outfit font-bold text-sage" data-testid="checkout-change">
                      {formatMXN(change)}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setCheckoutOpen(false)}
              data-testid="checkout-cancel"
              className="border-warm-border"
            >
              Cancelar
            </Button>
            <Button
              onClick={finalizeSale}
              disabled={processing}
              data-testid="checkout-confirm"
              className="bg-clay hover:bg-clay-hover text-white"
            >
              {processing ? "Registrando..." : "Confirmar venta"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ticket dialog */}
      {lastSale && (
        <Ticket
          open={ticketOpen}
          onOpenChange={setTicketOpen}
          sale={lastSale}
        />
      )}
    </div>
  );
}
